<template>
  <component
    ref="table"
    v-if="isTableComponentVisible"
    :is="component"
    :class="{ 'no-print': isQuickPrint }"
  ></component>
</template>

<script>
import { get, sync } from 'vuex-pathify';

import { capitalize } from '@/assets/js/utility';
import { camelCase } from 'lodash';

const components = {};
const requireComponent = require.context('@/components', false, /Table(?!Handler)(\w+|)\.(vue|js)$/);
requireComponent.keys().forEach(fileName => {
  const componentConfig = requireComponent(fileName);

  const componentName = capitalize(
    camelCase(
      fileName
        .split('/')
        .pop()
        .replace(/\.\w+$/, '')
    )
  );
  components[componentName] = componentConfig.default;
});

export default {
  name: 'TableHandler',
  components,
  data: () => ({
    isTableComponentVisible: true,
  }),
  methods: {
    async redrawTableComponent() {
      this.isTableComponentVisible = false;
      await this.$nextTick();
      this.isTableComponentVisible = true;
    },
  },
  computed: {
    layers: get('layers/layers'),
    currentLayer: sync('layers/currentLayer'),
    currentLayerDataSourceName: sync('layers/currentLayer@data_source_name'),
    watermetersDataSource: get('admin/modulesMapping@billing.watermeters_datasource_name'),
    failuresDataSource: get('admin/modulesMapping@failures.datasource_name'),
    zdmFailuresDataSource: get('admin/modulesMapping@zdm_failures.datasource_name'),
    tasksDataSource: get('admin/modulesMapping@tasks.datasource_name'),
    easementsDataSource: get('admin/modulesMapping@transmission_easement.datasource_name'),
    foldersDataSource: get('admin/modulesMapping@folders.datasource_name'),
    investmentsDataSource: get('admin/modulesMapping@investments.datasource_name'),
    dmaDataSource: get('admin/modulesMapping@billing.dma_datasource_name'),
    currentLayerDataSource: get('layers/currentLayer@data_source_name'),
    dmaLayerId: get('admin/modulesMapping@billing.dma_layer_id'),
    easementLayerId: get('admin/modulesMapping@transmission_easement.layer_id'),
    investmentsLayerId: get('admin/modulesMapping@investments.layer_id'),
    tasksLayerId: get('admin/modulesMapping@tasks.layer_id'),
    failuresLayerId: get('admin/modulesMapping@failures.layer_id'),
    zdmFailuresLayerId: get('admin/modulesMapping@zdm_failures.layer_id'),
    foldersLayerId: get('admin/modulesMapping@folders.layer_id'),
    watermetersLayerId: get('admin/modulesMapping@billing.layer_id'),
    isQuickPrint: get('tools/toolStatus@isQuickPrintToolActive'),
    layerId() {
      if (this.$route.params.lid) {
        return this.$route.params.lid;
      }
      const dict = {
        cutoffLayerTable: 'TableCutoffs',
        lockAnalysis: this.failuresLayerId,
        failure: this.failuresLayerId,
        zdm_failure: this.zdmFailuresLayerId,
        zdm_failures: this.zdmFailuresLayerId,
        failures: this.failuresLayerId,
        newFailure: this.failuresLayerId,
        task: this.tasksLayerId,
        tasks: this.tasksLayerId,
        transmission_easements: this.easementLayerId,
        transmission_easement: this.easementLayerId,
        folders: this.foldersLayerId,
        folder: this.foldersLayerId,
        investments: this.investmentsLayerId,
        investment: this.investmentsLayerId,
        legalization: this.watermetersLayerId,
        legalizationInfo: this.watermetersLayerId,
        waterDissolutionWatermeters: this.watermetersLayerId,
        waterDissolutionWatermetersInfo: this.watermetersLayerId,
        waterDissolutionDma: this.dmaLayerId,
        waterDissolutionDmaInfo: this.dmaLayerId,
        billingAnomalyDetection: this.watermetersLayerId,
        billingAnomalyDetectionInfo: this.watermetersLayerId,
      };
      return dict[this.currentRouteName];
    },
    currentRouteName() {
      return this.$route.name;
    },
    currentLayerId() {
      return this.$route.params.lid;
    },
    component() {
      const dataSourceNameDict = {
        [this.failuresDataSource]: 'TableFailures',
        [this.zdmFailuresDataSource]: 'TableZdmFailures',
        [this.tasksDataSource]: 'TableTasks',
        [this.easementsDataSource]: 'TableEasements',
        [this.foldersDataSource]: 'TableFolders',
        [this.investmentsDataSource]: 'TableInvestments',
        [this.dmaDataSource]: 'TableDma',
      };
      if (dataSourceNameDict[this.currentLayerDataSourceName]) {
        return dataSourceNameDict[this.currentLayerDataSourceName];
      }
      const dict = {
        cutoffLayerTable: 'TableCutoffs',
        lockAnalysis: 'TableFailures',
        failure: 'TableFailures',
        zdm_failure: 'TableZdmFailures',
        zdm_failures: 'TableZdmFailures',
        failures: 'TableFailures',
        newFailure: 'TableFailures',
        task: 'TableTasks',
        tasks: 'TableTasks',
        transmission_easements: 'TableEasements',
        transmission_easement: 'TableEasements',
        folders: 'TableFolders',
        folder: 'TableFolders',
        investments: 'TableInvestments',
        investment: 'TableInvestments',
        legalization: 'TableWatermeters',
        legalizationInfo: 'TableWatermeters',
        waterDissolutionDma: 'TableDma',
        waterDissolutionDmaInfo: 'TableDma',
        waterDissolutionWatermeters: 'TableWatermeters',
        waterDissolutionWatermetersInfo: 'TableWatermeters',
        billingAnomalyDetection: 'TableWatermeters',
        billingAnomalyDetectionInfo: 'TableWatermeters',
      };
      if (dict[this.currentRouteName]) {
        return dict[this.currentRouteName];
      }
      return 'TableBasic';
    },
  },
  watch: {
    layerId: {
      immediate: true,
      handler(nV) {
        if (!nV) {
          this.currentLayer = undefined;
          return;
        }
        this.currentLayer = this.layers[nV];
      },
    },
  },
  beforeDestroy() {
    this.currentLayer = undefined;
  },
  mounted() {
    this.$root.$on('redrawTableComponent', this.redrawTableComponent);
  },
};
</script>
